import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'ice' | 'ghost' | 'quiet' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  style?: CSSProperties;
}

const sizes: Record<ButtonSize, { padding: string; fontSize: number; height: number; gap: number; radius: number }> = {
  sm: { padding: '6px 10px', fontSize: 13, height: 28, gap: 6, radius: 6 },
  md: { padding: '8px 14px', fontSize: 14, height: 34, gap: 8, radius: 7 },
  lg: { padding: '12px 20px', fontSize: 15, height: 44, gap: 10, radius: 8 },
};

const variants: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: 'var(--ink-900)',
    color: 'var(--ink-000)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 1px 0 rgba(0,0,0,0.2)',
  },
  ice: {
    background: 'var(--ice-500)',
    color: 'var(--ice-on-fill)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), var(--glow-ice)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--fg)',
    border: '1px solid var(--border-strong)',
  },
  quiet: {
    background: 'var(--surface-2)',
    color: 'var(--fg)',
    border: '1px solid var(--border)',
  },
  danger: {
    background: 'var(--tint-fail-bg)',
    color: 'var(--tint-fail-fg)',
    border: '1px solid var(--tint-fail-br)',
  },
};

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  style,
  ...rest
}: ButtonProps) {
  const s = sizes[size];
  const v = variants[variant];
  return (
    <button
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: s.gap,
        padding: s.padding,
        fontSize: s.fontSize,
        height: s.height,
        borderRadius: s.radius,
        fontWeight: 500,
        letterSpacing: '-0.005em',
        transition: 'transform 120ms ease, opacity 120ms ease, background 120ms ease',
        cursor: 'pointer',
        ...v,
        ...style,
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
      onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}
